/* qeslint-disable */
// q@ts-nocheck
import {
    Cartographic,
    Cartesian3,
    Cesium3DTileset,
    Ellipsoid,
    HeadingPitchRoll,
    Matrix3,
    Matrix4,
    Math as CesiumMath,
    Quaternion,
    Transforms
} from "cesium";

import { AssetGeoLocation } from "../types/common";

export function georeferencedAtECEF(tileset: Cesium3DTileset) {
    if (tileset.boundingSphere.center.equals(Cartesian3.ZERO)) {
        return false;
    }

    // asset.extras.ion property is not available for some tileset generated by RealityCapture.
    const cartographic = Cartographic.fromCartesian(tileset.boundingSphere.center);

    //  Mariana Trench
    if (cartographic.height < -10994) {
        return false;
    }

    if (cartographic.height > 8848) {
        return false;
    }

    if (cartographic.longitude > CesiumMath.PI || cartographic.longitude < -CesiumMath.PI) {
        return false;
    }

    if (cartographic.latitude > CesiumMath.PI_OVER_TWO || cartographic.latitude < -CesiumMath.PI_OVER_TWO) {
        return false;
    }

    return true;
}

export function updateModelMatrixOfTilesetDefinedAtLocal(
    tileset: Cesium3DTileset,
    position: Cartesian3,
    refInLocal: Cartesian3,
    hpr: HeadingPitchRoll,
    scale: Cartesian3
) {
    const toWorld = Transforms.eastNorthUpToFixedFrame(position);

    const T = Matrix4.fromTranslation(Cartesian3.negate(refInLocal, new Cartesian3()));
    const R = Matrix4.fromRotation(Matrix3.fromQuaternion(Quaternion.fromHeadingPitchRoll(hpr)));
    const S = Matrix4.fromScale(scale, new Matrix4());

    const modelMatrix = toWorld;

    Matrix4.multiply(modelMatrix, R, modelMatrix);
    Matrix4.multiply(modelMatrix, S, modelMatrix);
    Matrix4.multiply(toWorld, T, modelMatrix);

    tileset.modelMatrix = modelMatrix;
}

export function updateModelMatrixOfTilesetDefinedAtECEF(options: {
    tileset: Cesium3DTileset;
    position: Cartesian3;
    rotateCenter: Cartesian3 | undefined; // defined at world
    geoRefAtLocal: Cartesian3 | undefined;
    hpr: HeadingPitchRoll;
    scale: Cartesian3;
}) {
    if (options.rotateCenter && options.geoRefAtLocal) {
        throw new Error("one required");
    }

    const tileset = options.tileset;
    const geoRefAtLocal = options.geoRefAtLocal;

    tileset.modelMatrix = Matrix4.IDENTITY;

    const origin = Cartesian3.clone(tileset.boundingSphere.center);
    const referenceFrame = Transforms.eastNorthUpToFixedFrame(origin);
    const toLocal = Matrix4.inverseTransformation(referenceFrame, new Matrix4());

    let T;
    let invT;

    if (options.rotateCenter) {
        const rotateCenter = Matrix4.multiplyByPoint(toLocal, options.rotateCenter, new Cartesian3());

        T = Matrix4.fromTranslation(Cartesian3.negate(rotateCenter, new Cartesian3()));
        invT = Matrix4.fromTranslation(rotateCenter);
    } else if (geoRefAtLocal) {
        T = Matrix4.fromTranslation(Cartesian3.negate(geoRefAtLocal, new Cartesian3()));
    }

    const R = Matrix4.fromRotation(Matrix3.fromQuaternion(Quaternion.fromHeadingPitchRoll(options.hpr)));
    const S = Matrix4.fromScale(options.scale, new Matrix4());
    const toWorld = Transforms.eastNorthUpToFixedFrame(options.position);

    const modelMatrix = toWorld;

    if (invT) {
        Matrix4.multiply(modelMatrix, invT, modelMatrix);
    }

    Matrix4.multiply(modelMatrix, R, modelMatrix);
    Matrix4.multiply(modelMatrix, S, modelMatrix);

    if (T) {
        Matrix4.multiply(modelMatrix, T, modelMatrix);
    }

    Matrix4.multiply(modelMatrix, toLocal, modelMatrix);

    tileset.modelMatrix = modelMatrix;
}

export function georeferenceTileset(tileset: Cesium3DTileset, geolocation: AssetGeoLocation) {
    const cartographic = Cartographic.fromDegrees(geolocation.longitude, geolocation.latitude, geolocation.height);
    const position = Ellipsoid.WGS84.cartographicToCartesian(cartographic);
    const hpr = new HeadingPitchRoll(
        CesiumMath.toRadians(geolocation.heading),
        CesiumMath.toRadians(geolocation.pitch),
        CesiumMath.toRadians(geolocation.roll)
    );

    const geoRefAtLocal = new Cartesian3();

    if (geolocation.localGeoRefX) {
        geoRefAtLocal.x = geolocation.localGeoRefX;
    }

    if (geolocation.localGeoRefY) {
        geoRefAtLocal.y = geolocation.localGeoRefY;
    }

    if (geolocation.localGeoRefZ) {
        geoRefAtLocal.z = geolocation.localGeoRefZ;
    }

    const scale = new Cartesian3(geolocation.scale.x, geolocation.scale.y, geolocation.scale.z);

    if (georeferencedAtECEF(tileset)) {
        updateModelMatrixOfTilesetDefinedAtECEF({
            tileset: tileset,
            position: position,
            rotateCenter: undefined,
            geoRefAtLocal: geoRefAtLocal,
            hpr: hpr,
            scale: scale
        });
    } else {
        updateModelMatrixOfTilesetDefinedAtLocal(tileset, position, geoRefAtLocal, hpr, scale);
    }
}
